// The Alcott Review — placeholder editorial + Horizon Circle content.
// NOTE: All copy and imagery here are elegant placeholders — replace with final assets.

export const ALCOTT = {
  name: "Sybils & Society",
  tagline: "Culture, memory, and the art of a considered life.",
  issue: "Volume I · The Inheritance Issue",
};

export const NAV = [
  { label: "The Oracle", to: "/" },
  { label: "Society Archives", to: "/archives" },
  { label: "Sybils Secrets", to: "/secrets" },
  { label: "See & Do", to: "/experiences" },
  { label: "The Horizon Circle", external: true, href: "https://thehorizoncircle.co" },
  { label: "Lineage", href: "https://www.findyatribe.org", external: true },
];

export const IMG = {
  horizonFeature: "/horizon-featured.webp",
  heroPortrait:
    "https://images.unsplash.com/photo-1542513217-0b0eedf7005d?crop=entropy&cs=srgb&fm=jpg&w=1500&q=85&ixlib=rb-4.1.0",
  portraitA:
    "https://images.unsplash.com/photo-1636144896336-b056be4a8dfe?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85&ixlib=rb-4.1.0",
  portraitB:
    "https://images.unsplash.com/photo-1600144559281-53d1dee79a99?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85&ixlib=rb-4.1.0",
  library1:
    "https://images.pexels.com/photos/1061130/pexels-photo-1061130.jpeg?auto=compress&cs=tinysrgb&w=1400",
  library2:
    "https://images.pexels.com/photos/6549372/pexels-photo-6549372.jpeg?auto=compress&cs=tinysrgb&w=1400",
  manuscript:
    "https://images.unsplash.com/photo-1724521260101-c13e61992638?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85&ixlib=rb-4.1.0",
  books:
    "https://images.unsplash.com/photo-1591197415080-d849883ecb27?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85&ixlib=rb-4.1.0",
  resort1:
    "https://images.unsplash.com/photo-1596473168207-06b970e8bfa3?crop=entropy&cs=srgb&fm=jpg&w=1500&q=85&ixlib=rb-4.1.0",
  resort2:
    "https://images.unsplash.com/photo-1596473085401-4e73c0b300ea?crop=entropy&cs=srgb&fm=jpg&w=1500&q=85&ixlib=rb-4.1.0",
  horizon1:
    "https://images.unsplash.com/photo-1596473085401-4e73c0b300ea?crop=entropy&cs=srgb&fm=jpg&w=1500&q=85&ixlib=rb-4.1.0",
  horizon2:
    "https://images.unsplash.com/photo-1783530668667-48a3c2b82de9?crop=entropy&cs=srgb&fm=jpg&w=1500&q=85&ixlib=rb-4.1.0",
  culinary1:
    "https://images.unsplash.com/photo-1760086626205-d7dcf45c0a4c?crop=entropy&cs=srgb&fm=jpg&w=1500&q=85&ixlib=rb-4.1.0",
  culinary2:
    "https://images.unsplash.com/photo-1600144559281-53d1dee79a99?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85&ixlib=rb-4.1.0",
  culinary3:
    "https://images.unsplash.com/photo-1636144896336-b056be4a8dfe?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85&ixlib=rb-4.1.0",
  secret1:
    "https://images.unsplash.com/photo-1591197415080-d849883ecb27?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85&ixlib=rb-4.1.0",
  secret2:
    "https://images.unsplash.com/photo-1724521260101-c13e61992638?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85&ixlib=rb-4.1.0",
};

// Veronica Bey's essays, incorporated from findyatribe.org (Urban American Indian Collective).
export const ESSAYS = [
  {
    id: "the-bath-club-miami-new-chapter",
    slug: "the-bath-club-miami-new-chapter",
    internal: true,
    category: "What's Tea · Miami",
    title: "The Bath Club: Who Holds the Key to Miami's Oldest Room",
    excerpt:
      "The Bath Club opened in 1926 as a sanctuary of exclusion. Nearly a century later it is stewarded by one of America's foremost Black development families — and the question it poses is the most interesting one in luxury: what happens when the people once kept out now hold the deed?",
    author: "Veronica Bey",
    readTime: "7 min",
    image:
      "https://static.prod-images.emergentagent.com/jobs/6191983f-92f1-4de9-9adc-9e0de9357616/images/446e3d8c5d98747fc5e2d53b9dab8904947aa5e19d77b2049a56c031718ed173.jpeg",
    imageCaption: "The Bath Club, Collins Avenue — a 1926 landmark, restored and reimagined.",
    body: [
      "There is a particular kind of building that Miami Beach does better than anywhere: the private club that faces the ocean and turns its back on almost everyone else. The Bath Club, opened on Collins Avenue in 1926, was the archetype — a Mediterranean-Revival sanctuary of cabanas and cotillions whose exclusivity was not incidental but the entire point. For most of the twentieth century, the guest list was a fortress. Black patrons and Jewish patrons were, by the ugly custom of the era, kept firmly outside the gate.",
      "So there is a deep and deliberate irony in who holds the key today. The landmark was acquired and painstakingly restored by R. Donahue Peebles and The Peebles Corporation — one of the most significant Black-owned real-estate development firms in the country — and it is now being carried into its next chapter by the next generation of that same family, Donahue Peebles III. A room built to exclude is stewarded by the very lineage it was designed to lock out. That is not a footnote. That is the whole story.",
      "What makes this more than a satisfying reversal is what it makes possible. Ownership is the difference between being admitted and setting the terms of admission. When a Black family holds the deed to a near-century-old institution, the questions change: who is on the membership committee, whose art hangs in the halls, which histories are told at the anniversary gala, and who profits when the cabanas fill. Access is a guest pass. Ownership is authorship.",
      "This is the intersection our readers understand instinctively — where heritage meets capital, and cultural relevance stops being something granted and becomes something governed. A restored Bath Club under Black stewardship is not a diversity gesture bolted onto old money; it is a demonstration that the most exclusive real estate in America can be held, run, and passed down by the people the old clubs tried to erase. The opportunity is not merely to be seen at the table. It is to own the room the table sits in.",
      "The Bath Club's second century has barely begun, and its meaning is still being written. But the premise is already the most quietly radical thing on Collins Avenue: that legacy is not the same as ownership, and that the future of luxury belongs to whoever is bold enough to hold the key — and generous enough to leave the gate open behind them.",
    ],
    sources: [
      { label: "The Bath Club — project history (Peebles Corporation)", url: "https://peeblescorp.com/project/the-bath-club" },
      { label: "Q&A: Donahue Peebles III on legacy & leadership (World Red Eye, 2026)", url: "https://worldredeye.com/2026/04/qa-carrying-the-torch-donahue-peebles-iii-on-legacy-leadership-and-miamis-future/" },
      { label: "About The Bath Club (official)", url: "https://thebathclubmb.com/about" },
    ],
  },
  {
    id: "turkey-and-eagle",
    category: "Symbolism",
    title: "The Turkey and the Eagle: Reclaiming the Feathered Symbolism of Indigenous America",
    excerpt:
      "While the Bald Eagle was codified by the United States as the ultimate emblem of \u201Cfreedom\u201D — its feathers held in sacred esteem by sovereign tribal nations — the turkey tells an older, truer story of Indigenous America.",
    author: "Veronica Bey",
    readTime: "10 min",
    image:
      "https://static.wixstatic.com/media/00f4cc_3858fd9f12d54a12a4baec1d6db32320~mv2.png/v1/fill/w_900,h_1100,fp_0.50_0.40,q_90,enc_avif,quality_auto/00f4cc_3858fd9f12d54a12a4baec1d6db32320~mv2.webp",
    url: "https://www.findyatribe.org/single-post/the-turkey-and-the-eagle-reclaiming-the-feathered-symbolism-of-indigenous-america",
  },
  {
    id: "lalovavi-black-opera",
    slug: "lalovavi-black-opera",
    internal: true,
    category: "Music",
    title: "LALOVAVI: Carrying the Tut Language onto the Opera Stage",
    excerpt:
      "On serving as the Tut language and cultural expert for LALOVAVI — Cincinnati Opera's Afrofuturist Black Opera Project debut, reviewed by The Wall Street Journal.",
    author: "Veronica Bey",
    readTime: "8 min",
    image:
      "https://static.prod-images.emergentagent.com/jobs/6191983f-92f1-4de9-9adc-9e0de9357616/images/b2bef7fcd70dc2aaca4e4784358ccd54e48a8abf907132282117dc45b0c0dd33.jpeg",
    imageCaption: "The Nunewaks, from LALOVAVI — Cincinnati Opera's Afrofuturist Black Opera Project world premiere. Image: Veronica Bey.",
    gallery: [
      {
        src: "https://customer-assets-gfyr7b9c.emergentagent.net/job_ethereal-council/artifacts/k737n55f_FB_IMG_1785978973081.webp",
        caption: "The company program — Tut Language Expert: Veronica Bey, of Niiji Consulting. Image: Veronica Bey.",
        wide: true,
      },
      {
        src: "https://static.prod-images.emergentagent.com/jobs/6191983f-92f1-4de9-9adc-9e0de9357616/images/9c9318cc683eacd29baad3b5476f1200588f6d673d4eb7745f11c2d7373780fb.jpeg",
        caption: "Opening night at Cincinnati Music Hall — the Summer Festival program and the Nunewaks cover art. Image: Veronica Bey.",
      },
      {
        src: "https://customer-assets-gfyr7b9c.emergentagent.net/job_ethereal-council/artifacts/0klrz063_FB_IMG_1785978962279.jpg",
        caption: "Veronica Bey on the carpet at The Black Opera Project premiere. Image: Veronica Bey.",
      },
    ],
    body: [
      "When Cincinnati Opera called about LALOVAVI, I understood at once that this was not an ordinary commission. LALOVAVI — the word means \u201Clove\u201D — is the inaugural work of the company's Black Opera Project: an Afrofuturist opera by composer Kevin Day and librettist Tifara Brown, set some four hundred years into the future in a luminous city that was once known as Atlanta. My charge was to steward something older than any of us in the room: the Tut language.",
      "Tut, sometimes written Tutnese, is a coded language cultivated by enslaved Indigenous Black Americans — an oral cipher that allowed a people to teach one another to read, spell, and speak freely in plain sight of those who had outlawed their literacy. It is not a novelty or a costume. It is a technology of survival, a grammar of resistance passed hand to mouth across generations, and it existed as a relic of defiance before the United States existed as a country.",
      "As LALOVAVI's Tut Language Expert — credited in the Cincinnati Opera program under my consultancy, Niiji Consulting — I coached the cast on far more than pronunciation. We worked the cadence and the breath of the language, yes — LALOVAVI is sung in both English and Tut, with the Tut projected in translation above the stage — but we spent just as long on the history and the dignity beneath each syllable. A singer cannot honor a word of resistance without first understanding what it cost to keep that word alive.",
      "That is the quiet argument of Afrofuturism, and of this opera: to place a language of concealment four centuries into the future is to insist that what was once hidden becomes heritage, and what was once coded becomes canon. The audience does not merely hear a story about Black joy and survival; they hear the very instrument of that survival lifted into aria.",
      "When The Wall Street Journal reviewed the premiere as \u201CThe Black Opera Project's Glittery Debut,\u201D I thought of every ancestor who spelled a forbidden word in Tut so that a child might one day read freely. To hear that language projected, sung, and reviewed in those pages is its own kind of emancipation — belated, but real.",
      "This is the work I am proudest to carry into Sybils & Society and, in time, into the salons of The Horizon Circle: culture moved across distance and across centuries, so that what our people encoded in the dark might finally be sung in the light.",
    ],
    sources: [
      {
        label: "The Wall Street Journal — \u201CThe Black Opera Project's Glittery Debut\u201D",
        url: "https://www.wsj.com/arts-culture/theater/lalovavi-review-the-black-opera-projects-glittery-debut-95ee2201",
      },
      {
        label: "Cincinnati Opera — LALOVAVI",
        url: "https://www.cincinnatiopera.org/lalovavi",
      },
      {
        label: "FindYaTribe — Chronological Overview of Linguistic Resistance and Academic Advancement: Indigenous Black Americans",
        url: "https://www.findyatribe.org/single-post/chronological-overview-of-linguistic-resistance-and-academic-advancement-indigenous-black-americans",
      },
    ],
  },
  {
    id: "pocahontas-island",
    category: "History",
    title: "Pocahontas Island, VA: Black\u2013Indigenous Roots, War Captives, and the First Escapes from Enslavement",
    excerpt:
      "Black\u2013Indigenous roots, war captives, and the first escapes from enslavement \u2014 the layered history of a Virginia island the colonial record tried to erase.",
    author: "Veronica Bey",
    readTime: "9 min",
    image:
      "https://static.wixstatic.com/media/00f4cc_c86ab955401b4c5db61194f84acc398a~mv2.jpg/v1/fill/w_900,h_1100,fp_0.50_0.50,q_90,enc_avif,quality_auto/00f4cc_c86ab955401b4c5db61194f84acc398a~mv2.webp",
    url: "https://www.findyatribe.org/single-post/pocahontas-island-va-black-indigenous-roots-war-captives-and-the-first-escapes-from-enslavement",
  },
  {
    id: "gullah-seminole",
    category: "Resistance",
    title: "The Gullah and Seminole Resistance: The Gullah Wars",
    excerpt:
      "Over a century of alliance between freedom-seeking Africans and Indigenous peoples in Florida \u2014 the \u201CGullah Wars\u201D remain one of history's most obscured chapters of resistance.",
    author: "Veronica Bey",
    readTime: "11 min",
    image:
      "https://static.wixstatic.com/media/00f4cc_87a0e9ad059f42e39039eebd7153964d~mv2.jpg/v1/fill/w_900,h_1100,fp_0.50_0.50,q_90,enc_avif,quality_auto/00f4cc_87a0e9ad059f42e39039eebd7153964d~mv2.webp",
    url: "https://www.findyatribe.org/single-post/the-gullah-and-seminole-resistance-a-unified-struggle-for-freedom-known-as-the-gullah-wars",
  },
  {
    id: "first-light-dance",
    category: "Culture",
    title: "Reclaiming the First Light: The Indigenous Power of Dance in Mashpee Wampanoag Territory",
    excerpt:
      "Across the territory of the Mashpee Wampanoag \u2014 the People of the First Light \u2014 a young American Aborigine reclaims identity in the vibrant, defiant language of traditional dance.",
    author: "Veronica Bey",
    readTime: "6 min",
    image:
      "https://static.wixstatic.com/media/00f4cc_0a9bdb20e63141968c169666f234a8ef~mv2.jpg/v1/fill/w_900,h_1100,fp_0.50_0.40,q_90,enc_avif,quality_auto/00f4cc_0a9bdb20e63141968c169666f234a8ef~mv2.webp",
    url: "https://www.findyatribe.org/single-post/reclaiming-the-first-light-the-indigenous-power-of-dance-in-mashpee-wampanoag-territory",
  },
  {
    id: "freeman-lineage",
    category: "Lineage",
    title: "Undoing the Division: The Indigenous Legacy of the Freeman Family Lineage",
    excerpt:
      "A deep dive into the ancestral records of the Freeman family of the Cape Fear region challenges centuries of colonial narratives about who was divided, and how.",
    author: "Veronica Bey",
    readTime: "9 min",
    image:
      "https://static.wixstatic.com/media/00f4cc_0e9f4eb55e184adca4e30f2ac93f1922~mv2.jpg/v1/fill/w_900,h_1100,fp_0.50_0.50,q_90,enc_avif,quality_auto/00f4cc_0e9f4eb55e184adca4e30f2ac93f1922~mv2.webp",
    url: "https://www.findyatribe.org/single-post/undoing-the-division-the-powerful-indigenous-legacy-of-the-freeman-family-lineage-findyatribe-or",
  },
  {
    id: "ai-behold-a-pale-horse",
    slug: "ai-behold-a-pale-horse",
    internal: true,
    category: "What's Tea · Futures",
    title: "Behold a Pale Algorithm: AI, Engineered Plague, and the Old Fear Made New",
    excerpt:
      "From smallpox blankets to Tuskegee to William Cooper's Behold a Pale Horse, the fear that disease can be authored and aimed is not paranoia — it is memory. What happens when the pen becomes an algorithm?",
    author: "Veronica Bey",
    readTime: "11 min",
    image:
      "https://static.prod-images.emergentagent.com/jobs/6191983f-92f1-4de9-9adc-9e0de9357616/images/db71536d7f439716651991dcb2af9742361d278ed02f242a48b12d3142bda7e7.jpeg",
    imageCaption: "\u201CAnd I looked, and behold a pale horse: and his name that sat on him was Death.\u201D — Revelation 6:8",
    body: [
      "In 1991, a former naval intelligence man named William Cooper published Behold a Pale Horse — a sprawling, feverish, deeply flawed book that nonetheless became scripture in corners of Black America and, especially, in hip-hop. It was quoted, sampled, and passed hand to hand not because every claim in it was true — many are demonstrably not — but because its central intuition landed on ground already salted with betrayal. Cooper argued that power engineers catastrophe and calls it nature. To a people who had lived through Tuskegee, that did not sound insane. It sounded familiar.",
      "This is the historical spine any honest conversation about AI and bioweapons must begin with. The idea that disease can be authored, hoarded, and aimed is not science fiction and it is not conspiracy; it is documented history. Colonizers understood contagion as a weapon against Indigenous nations long before germ theory had a name. The United States Public Health Service watched Black men die of untreated syphilis for forty years and called it a study. Biological warfare programs were real, funded, and hidden. The fear, in other words, was earned.",
      "So when the current headlines warn that artificial intelligence might help design novel pathogens, our communities do not hear a new anxiety. We hear an old one, re-clothed in code. That cultural inheritance is a kind of expertise: a hard-won refusal to assume good faith from institutions that have not earned it. But it can also curdle into fatalism, and here the record demands precision. In 2024, a controlled RAND study found that today's large language models offered no meaningful uplift to someone attempting to plan a biological attack — the models were, in effect, no better than a search engine. The nearer, graver concern that scientists in Nature have flagged is not the chatbot but the specialized biological design tool: software that could one day help engineer pathogens with properties nature never made. The threat is real, it is future-facing, and it is governable — if we choose to govern it.",
      "What Cooper got right, mythically, was the horse. 'Behold a pale horse,' Revelation says, 'and his name that sat on him was Death, and Hell followed with him.' The pale rider is pestilence, and in the oldest tellings pestilence is never separate from the land. This is the piece our environmental thinkers have insisted on since Rachel Carson: there is no clean line between a weapon aimed at people and a wound dealt to the living world. A pathogen released does not respect a border, a species, or a watershed. Gain-of-function ambition and ecological collapse are the same story told at different scales — the fantasy that we can edit life without answering to it.",
      "That is the cultural stakes, and they are ours specifically. The communities most experimented upon, most exposed to environmental harm, most likely to be told a poisoning is a coincidence — these are the communities with the clearest view of what unaccountable power does with new tools. Our task is not to retreat into Cooper's paranoia nor to dissolve into the tech industry's optimism, but to hold the harder, more matriarchal position: that stewardship is the only ethic that survives contact with a technology this total. You do not hand a loaded future to institutions that have not made amends.",
      "The pale horse is not a prophecy we are helpless before. It is a warning about what we build and who we let hold the reins. Artificial intelligence will not, by itself, ride it in. People will — or people will refuse to. Behold, then, not the horse, but the hands. And ask, as our grandmothers always did before they trusted anyone with the water or the seed: whose interest does this serve, and who pays if it goes wrong?",
    ],
    sources: [
      { label: "William Cooper, \u201CBehold a Pale Horse\u201D (context & critique)", url: "https://en.wikipedia.org/wiki/Behold_a_Pale_Horse_(book)" },
      { label: "RAND (2024): red-teaming the risks of AI in biological attacks", url: "https://www.rand.org/pubs/research_reports/RRA2977-2.html" },
      { label: "Nature (2024): AI could pose pandemic-scale biosecurity risks", url: "https://www.nature.com/articles/d41586-024-03815-2" },
      { label: "Nuclear Threat Initiative — Biosecurity & AI governance", url: "https://www.nti.org/area/biological/" },
      { label: "Harriet A. Washington, \u201CMedical Apartheid\u201D", url: "https://en.wikipedia.org/wiki/Medical_Apartheid" },
      { label: "Rachel Carson, \u201CSilent Spring\u201D", url: "https://en.wikipedia.org/wiki/Silent_Spring" },
    ],
  },
];

// ARCHIVE STRAND — Modern Spirit (on-site essays by Veronica Bey)
export const ARCHIVE_SPIRITUAL = [
  {
    id: "the-altar-within",
    slug: "the-altar-within",
    internal: true,
    category: "Modern Spirit",
    title: "The Altar Within: A Modern Black Spirituality",
    excerpt:
      "On building a devotional life on our own terms — where the sacred is not inherited from an empire but remembered from the ancestors.",
    author: "Veronica Bey",
    readTime: "6 min",
    image:
      "/altar-cake.jpg",
    body: [
      "A modern spirituality does not require us to abandon reason for reverence. It asks only that we make a little room — a shelf, a windowsill, a corner of the morning — where the sacred can be tended without apology.",
      "For Black women especially, the altar within is an act of return: to the water rituals, the herb knowledge, the call-and-response between the living and the departed. We are not inventing a faith. We are remembering one.",
    ],
  },
  {
    id: "keeping-the-ancestors-close",
    slug: "keeping-the-ancestors-close",
    internal: true,
    category: "Modern Spirit",
    title: "Keeping the Ancestors Close",
    excerpt:
      "Daily ancestor work as spiritual practice — how a photograph, a glass of water, and a spoken name become a discipline of belonging.",
    author: "Veronica Bey",
    readTime: "5 min",
    image:
      "https://images.unsplash.com/photo-1594737997205-89877cb12185?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85&ixlib=rb-4.1.0",
    body: [
      "Ancestor work is not a séance; it is a habit. A photograph turned to face the room. A glass of clear water refreshed each morning. A name spoken aloud so it is not allowed to fall silent.",
      "This is spirituality as maintenance — the quiet, repeated tending of a relationship that death did not end. To keep the ancestors close is to keep ourselves accountable to the ones who dreamed us before we arrived.",
    ],
  },
];

// ARCHIVE STRAND — Gullah Geechee history (findyatribe.org)
export const ARCHIVE_GULLAH = [
  {
    id: "gullah-indigenous-ties",
    category: "Gullah Geechee",
    title: "Unraveling the Indigenous Ties of Gullah Geechee Culture",
    excerpt:
      "The Indigenous roots beneath the Gullah language and lifeways — and how a Lowcountry people kept an entire culture intact against every intention to erase it.",
    author: "Veronica Bey",
    readTime: "10 min",
    image:
      "https://static.wixstatic.com/media/00f4cc_87a0e9ad059f42e39039eebd7153964d~mv2.jpg/v1/fill/w_900,h_1100,fp_0.50_0.50,q_90,enc_avif,quality_auto/00f4cc_87a0e9ad059f42e39039eebd7153964d~mv2.webp",
    url: "https://www.findyatribe.org/single-post/unraveling-the-indigenous-ties-of-gullah-geechee-culture",
  },
  {
    id: "gullah-seminole-wars",
    category: "Gullah Geechee",
    title: "The Gullah and Seminole Resistance: The Gullah Wars",
    excerpt:
      "Over a century of alliance between freedom-seeking Africans and Indigenous peoples in Florida — one of history's most obscured chapters of resistance.",
    author: "Veronica Bey",
    readTime: "11 min",
    image:
      "https://static.wixstatic.com/media/00f4cc_e47fc53b584a4926bc1ed165cd400d2e~mv2.jpg/v1/fill/w_900,h_1100,fp_0.50_0.50,q_90,enc_avif,quality_auto/00f4cc_e47fc53b584a4926bc1ed165cd400d2e~mv2.webp",
    url: "https://www.findyatribe.org/single-post/the-gullah-and-seminole-resistance-a-unified-struggle-for-freedom-known-as-the-gullah-wars",
  },
];

// ARCHIVE STRAND — Heritage & Fashion (unique Black American heritage)
export const ARCHIVE_FASHION = [
  {
    id: "the-crown-we-tie",
    slug: "the-crown-we-tie",
    internal: true,
    category: "Heritage & Fashion",
    title: "The Crown We Tie: A History of the Headwrap",
    excerpt:
      "From the Tignon Laws of colonial Louisiana to the runway — how a garment meant to diminish Indigenous Black American women became a coronet of defiance.",
    author: "Veronica Bey",
    readTime: "8 min",
    image:
      "https://images.unsplash.com/photo-1613876215075-276fd62c89a4?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85&ixlib=rb-4.1.0",
    body: [
      "In 1786, colonial Louisiana passed the Tignon Laws — Governor Esteban Rodríguez Miró's edict ordering Indigenous Black American women, free and enslaved alike, to bind their hair beneath a tignon, a headwrap, so that they might be marked as lesser and their beauty blunted in the public eye.",
      "The law was born of fear. The women of color of New Orleans — many of them free, propertied, and strikingly elegant — were said to be drawing too much admiration, unsettling the fragile racial order of the colony. Their adorned hair, jeweled and dressed, was read as a provocation. To cover it was meant to return them, visibly, to their assigned place.",
      "The women complied, and then defied. They tied their tignons in silk and madras, in Caribbean brights and imported jewels, folding and towering them into architecture. What was meant to diminish became ornament; what was meant to shame became a crown. The edict backfired so completely that the tignon became a signature of the very glamour it had set out to suppress — proof that you cannot legislate a people's beauty out of existence.",
      "That is the recurring genius of Indigenous Black American fashion: to take what was imposed and re-author it as glory. From the tignon to the Sunday-morning headwrap to the runways of contemporary designers, the covered head has carried a coded message — that these women, and no one else, would decide what their beauty means.",
      "The headwrap remains a heritage garment precisely because it holds that memory: a sentence turned into a statement, surveillance turned into style. Every woman who ties one today — whether she knows the history or not — repeats an old and sovereign act of refusal. The crown was never given. It was tied.",
    ],
    sources: [
      {
        label: "Continued reading — The Tignon and the history of the Tignon Laws",
        url: "https://en.wikipedia.org/wiki/Tignon",
      },
    ],
  },
  {
    id: "indigo-and-sweetgrass",
    slug: "indigo-and-sweetgrass",
    internal: true,
    category: "Heritage & Fashion",
    title: "Indigo & Sweetgrass: Dressed in the Lowcountry",
    excerpt:
      "The material heritage of the Gullah Geechee coast — indigo-dyed cloth, sweetgrass adornment, and the quiet couture of a self-sufficient people.",
    author: "Veronica Bey",
    readTime: "6 min",
    image:
      "https://images.unsplash.com/photo-1530785602389-07594beb8b73?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85&ixlib=rb-4.1.0",
    body: [
      "Before indigo was a trend it was a labor and a language of the Lowcountry — the deep blue that Gullah Geechee hands coaxed from leaf to vat to cloth. To wear it was to wear the color of one's own expertise.",
      "Paired with sweetgrass woven into baskets and adornment, Lowcountry dress was a quiet couture: self-sufficient, exacting, and unmistakably one's own. Heritage fashion, at its truest, is the record of a people who clothed themselves in their own skill.",
    ],
  },
  {
    id: "jackee-harry-ode",
    slug: "jackee-harry-ode",
    internal: true,
    category: "Heritage & Fashion · Icon",
    title: "An Ode to Jackée: Satin, Fur & the Last Word",
    excerpt:
      "A love letter to Jackée Harry — the sitcom icon who wore a marabou stole like a manifesto and taught a generation of Black women that glamour is a form of comic timing.",
    author: "Veronica Bey",
    readTime: "5 min",
    image: "https://static.prod-images.emergentagent.com/jobs/6191983f-92f1-4de9-9adc-9e0de9357616/images/13dea7a78ff0d09e019f63ac153f7392634d2e162acee900c89978d971c10004.jpeg",
    imageCaption: "White fox, liquid satin, a corona of curls — Jackée arriving, always, on her own terms.",
    body: [
      "Some women enter a room; Jackée Harry made an entrance a genre. Before she said a word you had the whole thesis: the tower of curls, the white fur slipping off one shoulder, the satin catching every light in the house. As Sandra Clark on 227, she turned the doorway into a runway and the double-take into a punchline — and in 1987 she became the first Black woman to win an Emmy for Outstanding Supporting Actress in a Comedy Series. The trophy was for the acting. The legend was for the look.",
      "Her style was a beautiful contradiction, and that was the point. The marabou and the mink read as pure Hollywood fantasy, but the delivery — the smirk, the slow turn, the eyebrow — kept it human and hilarious. Jackée understood something our grandmothers knew: that a Black woman's glamour is never only decorative. It is armor, it is comedy, it is the last word in an argument nobody realized they were losing.",
      "Look at the wardrobe and you find the eighties at their most gloriously unbothered — a strapless column of ruched satin, a print dress in carnival brights with a mermaid hem, a slim gown worn with a jacket thrown over the shoulders like an afterthought. Nothing shy. Everything intentional. She dressed the way she acted: big, warm, and completely in command of the frame.",
      "What we owe her is the permission she modeled — to be sensual and silly, adorned and in charge, all at once. She made vanity generous. She made big hair a thesis statement. She let Black women see, on primetime, a version of themselves that was allowed to take up the whole doorway.",
      "So this is the ode: to Jackée, who never once dimmed herself to fit the room, and who proved that elegance, in the right hands, can be the funniest thing in it. Long may she wear the fur.",
    ],
    gallery: [
      { src: "https://static.prod-images.emergentagent.com/jobs/6191983f-92f1-4de9-9adc-9e0de9357616/images/4fda0176c0dde47297d21930d5b10d4121065d067b1944c340a1c318c151ebe1.jpeg", caption: "Carnival brights and a mermaid hem — color as pure joy." },
      { src: "https://static.prod-images.emergentagent.com/jobs/6191983f-92f1-4de9-9adc-9e0de9357616/images/4faa04d60ee4b7dc1b2be8c2eb07cb09d58d0119b838ccdf9bd82aee85638c01.jpeg", caption: "A slim gown, a jacket off the shoulder — glamour worn like a shrug." },
    ],
    sources: [
      { label: "Jackée Harry — life & work (Wikipedia)", url: "https://en.wikipedia.org/wiki/Jack%C3%A9e_Harry" },
    ],
  },
];

// ARCHIVE STRAND — Black American Art & Cities across the United States
export const ARCHIVE_ART_CITIES = [
  {
    id: "raising-the-deep-cthulhu",
    slug: "raising-the-deep-cthulhu",
    internal: true,
    category: "Art · Contemporary",
    title: "She Who Raises the Deep",
    excerpt:
      "On Jamea Richardson-Edwards's 'The Resurrection of Cthulhu' — a collage summoning in which Veronica Bey stands among the women who wake the old, drowned god and bend its dread to their will.",
    author: "Veronica Bey",
    readTime: "6 min",
    image:
      "https://static.prod-images.emergentagent.com/jobs/6191983f-92f1-4de9-9adc-9e0de9357616/images/65cdb236711f0ad1a11ac1614a5cad9a4ce5eb7aafc4736707ce640d9b1c19eb.jpeg",
    imageCaption: "Veronica in The Resurrection of Cthulhu. Artwork: Jamea Richardson-Edwards.",
    body: [
      "There is an old dread that the West keeps in its literature — a drowned, tentacled god dreaming beneath the sea, waiting to rise and unmake the small order of men. Lovecraft imagined it as horror. Jamea Richardson-Edwards imagines it as inheritance. In 'The Resurrection of Cthulhu,' the artist does not run from the leviathan; she conjures it, and she puts Black women at the center of the summoning.",
      "I did not expect to meet myself in a myth. Yet there I am — braided, watchful, rendered in Richardson-Edwards's dense collage of marbled paper, fabric, gesture, and paint — one of three figures standing at the threshold between the seen world and the churning, iridescent thing rising out of the deep. The eclipsed yellow sun hangs overhead like an opened eye. The creature uncoils in violet and cobalt, spitting light. Nobody in the frame is afraid.",
      "That is the esoteric turn of the work, and its power: it takes the oldest colonial fear — that what has been buried will return, monstrous — and reads it instead as resurrection. The deep is not the enemy. The deep is memory. It is the middle passage and everything the water swallowed, refusing to stay drowned. To raise Cthulhu, in Richardson-Edwards's hands, is to raise the ancestors, and the women in the piece are not victims of the rising. They are its priestesses.",
      "Richardson-Edwards works the way a rootworker works — layering, cutting, binding disparate materials into a single charged surface until the picture hums. Her marbled grounds swirl like scrying water; her patterns quote the textiles of home; her figures hold their gaze with the flat, sovereign calm of women who have already seen the end and decided to preside over it. To sit for such an artist is to be entered into a spell you did not write but are honored to complete.",
      "So consider this the record: I am in the painting. Not as muse, not as ornament, but as one of the ones doing the work — a matriarch at the rim of the abyss, hand steady, calling the old god up by name. The Society has always taught that power is a thing remembered before it is a thing performed. Here, on a wall, in collage, is the proof. The deep answers when a woman knows what to call it.",
    ],
    gallery: [
      {
        src: "https://static.prod-images.emergentagent.com/jobs/6191983f-92f1-4de9-9adc-9e0de9357616/images/e010d2bb9f8127933e353b45e60587bd5830e09509ff53924504d0f6457aa702.jpeg",
        caption: "The full diptych — 'The Resurrection of Cthulhu.' Artwork: Jamea Richardson-Edwards.",
        wide: true,
      },
    ],
  },
  {
    id: "neighbors-in-the-sky",
    slug: "neighbors-in-the-sky",
    internal: true,
    category: "Cities · Sunny Isles Beach",
    title: "Heeey, Neighbor: A Night in Sunny Isles Beach",
    excerpt:
      "A run-in with Savannah James at the Publix, a tower of quiet money across the boulevard, and the particular tea of living where old ocean and new wealth keep the same address.",
    author: "Veronica Bey",
    readTime: "5 min",
    image:
      "https://static.prod-images.emergentagent.com/jobs/6191983f-92f1-4de9-9adc-9e0de9357616/images/228d4302da64addae70c92fdc542bd837418d653274ee7dd469b35b098d05dd3.jpeg",
    imageCaption: "The Mansions at Acqualina, Sunny Isles Beach — where the ocean and the neighbors are both very quiet money.",
    body: [
      "I went to the Publix tonight and ran into the Savannah James. No security, no entourage — just a woman moving through the produce aisle like the rest of us, choosing her evening with her own two hands. I did the polite thing and kept my composure, which is to say I did not make a scene, though the scene was certainly making itself in my head.",
      "Now, I already knew that Sunny Isles Beach sits among the wealthiest zip codes in the country — a slender ribbon of Collins Avenue where the towers stand shoulder to shoulder like a receiving line, each one keeping the ocean to itself. What I had not fully registered, until tonight, is that the James family are, quite literally, my neighbors. I googled to be sure. Sure enough: across the boulevard, at the Mansions at Acqualina, they keep a residence I don't even need to zoom in to see.",
      "There is a particular tea to this stretch of coast. The money here does not shout; it hums. It arrives by valet and departs by tinted glass. It takes its evening swim on a rooftop that hovers over the skyline, a cold drink sweating on the rail, the city arranged below like a set. It is the kind of wealth that has learned to be quiet — which, if you have any home training, you understand is the loudest wealth of all.",
      "I am not saying I've made it. I know better than to confuse a good address for an arrival. But there is something to be said for standing at your own window at night, watching the tower across the street glow floor by floor, and understanding that the distance between you and the people inside it is one boulevard wide. Proximity is not the same as parity — and still, proximity teaches you what is possible. It rearranges what you allow yourself to want.",
      "So consider this a dispatch from the seventeenth floor: the ocean was calm, the produce was fresh, the neighbors were gracious, and the view was — as the young people say — a whole mood. I might not have made it yet. But I am, at the very least, in the place to make it. Heeey, neighbor. Lol.",
    ],
    gallery: [
      {
        src: "https://static.prod-images.emergentagent.com/jobs/6191983f-92f1-4de9-9adc-9e0de9357616/images/fada6dcf0a5ae1ce26832a94dcf24a900d307c6de4d2716d603b5521ac066445.jpeg",
        caption: "The tower, from below — Sunny Isles keeps its crown jewels in the sky.",
      },
      {
        src: "https://static.prod-images.emergentagent.com/jobs/6191983f-92f1-4de9-9adc-9e0de9357616/images/0a8448c38136f7f7419826c03fc6ec6b162936a5ab6bbe86aace74709312d31e.jpeg",
        caption: "A cold drink on the rooftop, the skyline arranged like a set.",
      },
      {
        src: "/neighbors-moon-upright.jpg",
        caption: "a summer full moon view.",
      },
    ],
  },
  {
    id: "the-wall-speaks",
    slug: "the-wall-speaks",
    internal: true,
    category: "Art & Cities",
    title: "The Wall Speaks: Black Muralism in the American City",
    excerpt:
      "From Chicago's Wall of Respect to the gable ends of the South — how the mural became Black America's most public and most defiant canvas.",
    author: "Veronica Bey",
    readTime: "7 min",
    image:
      "https://images.unsplash.com/photo-1777177496990-f598df7aac78?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85&ixlib=rb-4.1.0",
    body: [
      "In 1967, on the South Side of Chicago, a group of artists painted the Wall of Respect — a mural of Black heroes on the side of an ordinary building, unsanctioned and unforgettable. It made the wall a witness, and it made the city a gallery no one could gatekeep.",
      "That impulse still travels the country, gable end to overpass, from Philadelphia to New Orleans. Black muralism insists that art belongs to the block that raised it — that the most public canvas is also the most honest.",
    ],
  },
  {
    id: "a-map-of-black-america",
    slug: "a-map-of-black-america",
    internal: true,
    category: "Art & Cities",
    title: "A Map of Black America: Harlem to the Ninth Ward",
    excerpt:
      "A cultural cartography of the cities that shaped us — Harlem, Chicago's Bronzeville, New Orleans' Tremé — where geography became genius.",
    author: "Veronica Bey",
    readTime: "8 min",
    image:
      "https://images.unsplash.com/photo-1766792235377-cf392429fb84?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85&ixlib=rb-4.1.0",
    body: [
      "There is a map of Black America drawn not in borders but in blocks: Harlem's brownstones, Bronzeville's stroll, the Tremé's second lines, the Ninth Ward's stubborn return. Each is a district where geography became genius.",
      "To travel this map is to understand that our art was never separable from our cities. The neighborhood was the studio, the porch was the salon, and the street itself was the first stage. Culture, here, has an address.",
    ],
  },
  {
    id: "charleston-city-guide",
    internal: false,
    url: "https://thehorizoncircle.co/city-guides/charleston",
    category: "Cities · Charleston",
    title: "Charleston: The Atlantic Arrival Point",
    excerpt:
      "Gullah Geechee Charleston — the Atlantic arrival point. Black-owned houses and tables, the museums worth your morning, and the history most guides leave out. A city guide from The Horizon Circle.",
    author: "The Horizon Circle",
    readTime: "City guide",
    image:
      "https://images.unsplash.com/photo-1701459743222-60b6486974c7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    id: "savannah-city-guide",
    internal: false,
    url: "https://thehorizoncircle.co/city-guides/savannah",
    category: "Cities · Savannah",
    title: "Savannah: First African Baptist & the Rice Coast",
    excerpt:
      "Gullah Geechee Savannah — First African Baptist and the rice coast. The historic districts, the tables, and the record most guides omit. A city guide from The Horizon Circle.",
    author: "The Horizon Circle",
    readTime: "City guide",
    image:
      "https://images.unsplash.com/photo-1780348545480-3aa0bd3de7de?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
];

// SACRED SCROLLS — book reviews of old, out-of-print magical texts (academic, culturally rooted).
export const SACRED_SCROLLS = [
  {
    id: "vodou-quantum-leap-crosley",
    slug: "vodou-quantum-leap-crosley",
    internal: true,
    featured: true,
    category: "Sacred Scrolls · Feature",
    title: "The Vodou Quantum Leap: When the Ancestors Read Physics",
    excerpt:
      "Long before the multiverse became a summer blockbuster, Dr. Reginald Crosley argued that Haitian Vodou had already mapped it. A deep read of the book that treats the crossroads as a wormhole and the lwa as travelers between worlds.",
    author: "Veronica Bey",
    readTime: "12 min",
    image:
      "https://static.prod-images.emergentagent.com/jobs/6191983f-92f1-4de9-9adc-9e0de9357616/images/ad2a2e6b91628c51c13658a26f22ca2d39c6a715fa4f7da08e32600b673182fb.jpeg",
    imageCaption: "The voltigeur at the threshold — Vodou's leaper between parallel worlds.",
    body: [
      "In 2022, an entire cinema wept and cheered as a laundromat owner learned to jump between parallel versions of her own life. Everything Everywhere All at Once made the multiverse feel intimate, domestic, almost spiritual. What almost no one in those theaters knew was that a Haitian-American physician had, two decades earlier, written a book insisting that his grandmother's religion had been doing exactly this all along — and calling it by its right name.",
      "That book is The Vodou Quantum Leap: Alternative Realities, Power, and Mysticism, published in 2000 by Dr. Reginald Crosley (1937–2020), a practicing internist and nephrologist who spent his professional life inside Western medicine and his interior life inside the cosmology of his ancestors. Crosley's audacious thesis is that operational Vodou — the working kind, practiced in secret societies, not the tourist kind — is not superstition dressed as faith but a form of applied physics. The crossroads is a coordinate. The lwa are travelers. The ceremony is an interface.",
      "His central figure is the voltigeur, the 'magical leaper' who can cross between the manifold dimensions the priests always insisted were there. Read that against a culture currently obsessed with Marvel's multiverse and Spider-Man's spider-verse, and something uncanny happens: the pop-cultural fantasy of dimension-hopping starts to look like a diluted, computer-generated echo of a much older claim. Crosley's move is to take the ancient claim seriously — to line it up beside dark matter, parallel universes, and the stranger corners of quantum theory, and ask whether the mambos were describing the same territory the physicists are only now naming.",
      "The book is at its most hypnotic when it turns to the architecture of the soul. Crosley distinguishes the semido — the aspect that keeps its earthly hungers for food, sex, and music — from the selido, which does not return to the living. He describes the zin, a vessel holding hair or nail, a biological anchor kept alive through periodic rituals of fire. To a generation raised on cloud backups, DNA ancestry kits, and the dream of digital immortality, the zin reads less like folklore and more like a prophecy: the intuition that a person can be stored, tethered, and revived through a fragment of the body. Silicon Valley calls it 'the singularity.' Haiti called it the zin, and it was tending the servers by candlelight centuries first.",
      "Crosley does not flinch from the fearsome material either — the red sects, the Bizango and Makaya, the mechanics of possession and channeling. This is where a lazier writer would reach for spectacle, and where he instead reaches for structure, treating possession as a kind of bandwidth, a body opened as a receiver. It is impossible to read these chapters in the age of Afrofuturism — of Sun Ra's cosmic exile, of Janelle Monáe's android messiahs, of Wakanda's ancestral plane — without recognizing the same insistence running underneath all of it: that Black spiritual traditions were never behind science. They were ahead of it, speaking in a grammar the West hadn't built the instruments to hear yet.",
      "For all its ambition, the book asks to be read with both hands — one holding the wonder, one holding the discernment. Crosley is a synthesist, and synthesists overreach; the quantum metaphors sometimes gallop past what the equations can bear, and a careful reader will feel the seams between the poetry and the peer review. But that tension is the point. The Vodou Quantum Leap is not trying to win a physics argument. It is trying to restore dignity to a cosmology that colonizers spent centuries mocking — to say that a people who were told their gods were devils had, in fact, been keeping a map of the universe.",
      "That is why it belongs on our shelf. This is a matriarchal book in the deepest sense: it treats inherited, embodied, ancestral knowledge as data, and it refuses the modern insult that mystery and intelligence are opposites. Read it beside the multiverse you already love from the movies, and let it do the older, harder work — wrapping tonight's cultural fascination back into the ancient mystery it forgot it came from. The ancestors, Crosley insists, were reading physics all along. We are only now catching up to the syllabus.",
    ],
    downloads: [
      { label: "Full scan — 2000 first edition (Archive.org)", url: "https://archive.org/details/vodouquantumleap00regi" },
    ],
    sources: [
      { label: "The book on OpenLibrary (editions & details)", url: "https://openlibrary.org/books/OL8672622M/The_Vodou_Quantum_Leap_Alternative_Realities_Power_and_Mysticism" },
      { label: "2022 Tribute Edition (Theion Publishing)", url: "https://theionpublishing.com/shop/vql-tribute/" },
      { label: "Everything Everywhere All at Once — the multiverse, on film", url: "https://en.wikipedia.org/wiki/Everything_Everywhere_All_at_Once" },
      { label: "Afrofuturism — context & lineage", url: "https://en.wikipedia.org/wiki/Afrofuturism" },
    ],
  },
  {
    id: "sixth-seventh-books-of-moses",
    slug: "sixth-seventh-books-of-moses",
    internal: true,
    category: "Sacred Scrolls · Review",
    title: "The Sixth and Seventh Books of Moses",
    excerpt:
      "The grimoire that crossed the Atlantic and quietly became a cornerstone of Hoodoo. A review of the seals, the psalms, and the German folk magic our grandmothers made their own.",
    author: "Veronica Bey",
    readTime: "7 min",
    image:
      "https://static.prod-images.emergentagent.com/jobs/6191983f-92f1-4de9-9adc-9e0de9357616/images/d56b93be21a39fb46c45b514a4c600f126e6a1feed3ab10edf5e7d34ffea2239.jpeg",
    imageCaption: "A working text, not a museum piece — read by candle, kept under the mattress.",
    body: [
      "Few books have been more feared, more banned, and more beloved than The Sixth and Seventh Books of Moses. Attributed — with a wink — to the prophet himself, it is in truth a German folk grimoire, stitched together in the eighteenth and nineteenth centuries from Kabbalistic seals, Latin conjurations, and the Psalms. Johann Scheibel's 1849 edition sent it into the world; the American occult publisher L.W. de Laurence sent it everywhere else.",
      "What matters, for us, is where it landed. Carried into the American South, the book was absorbed into Hoodoo and rootwork so completely that generations of practitioners treated it as scripture — the seals copied out by hand, the psalms prescribed like remedies, the whole volume kept hidden because it was understood to be powerful. That is the review that counts: not whether the magic 'works,' but that a displaced people made a displaced book their own, and in doing so authored a tradition.",
      "As a text, it is gloriously uneven — part spell-book, part devotional, part printer's opportunism. The famous seals are woodcut mandalas of divine names; the instructions are terse to the point of comedy. Read it as literature and you will be frustrated. Read it as a record of how faith and necessity fuse under pressure, and it is one of the most important books in the African American esoteric canon.",
      "Our verdict: essential, and best read alongside a good folklorist. Approach the seals as art and the psalms as poetry, and let the history do the haunting.",
    ],
    downloads: [
      { label: "Full scan — De Laurence edition (PDF, Archive.org)", url: "https://archive.org/download/sixthseventhbook00dela/sixthseventhbook00dela.pdf" },
    ],
    sources: [
      { label: "Read the full text (Sacred Texts Archive)", url: "https://www.sacred-texts.com/grim/mos/index.htm" },
      { label: "Scan & editions overview (Archive.org)", url: "https://archive.org/details/the-sixth-and-seventh-books-of-moses" },
      { label: "Background & scholarship (Wikipedia)", url: "https://en.wikipedia.org/wiki/Sixth_and_Seventh_Books_of_Moses" },
    ],
  },
  {
    id: "greater-key-of-solomon",
    slug: "greater-key-of-solomon",
    internal: true,
    category: "Sacred Scrolls · Review",
    title: "The Key of Solomon the King",
    excerpt:
      "Mathers' 1889 translation of the great Solomonic grimoire — pentacles, planetary hours, and the fussy, thrilling choreography of Renaissance magic. A review of the book that taught the West to draw a circle.",
    author: "Veronica Bey",
    readTime: "6 min",
    image:
      "https://static.prod-images.emergentagent.com/jobs/6191983f-92f1-4de9-9adc-9e0de9357616/images/e69f33d236f8ef3080cd52835db7016b27de098081c710f49f02d30c18a42f15.jpeg",
    imageCaption: "Clavicula Salomonis — the manuscript tradition Mathers dragged into print.",
    body: [
      "The Key of Solomon is the grimoire other grimoires cite. Circulating in Latin, Italian, and French manuscripts for centuries, it was finally Englished in 1889 by S.L. MacGregor Mathers — co-founder of the Golden Dawn — from manuscripts held in the British Museum. His edition is imperfect, occasionally prudish, and utterly foundational.",
      "What you get is a manual: how to prepare, when to work (the planetary hours are exacting), and the celebrated pentacles — those elegant discs of Hebrew letters and geometry that have decorated everything from occult revivals to album covers. It is a book obsessed with procedure, and there is something moving in that obsession: the belief that if you are only precise enough, orderly enough, the cosmos will answer.",
      "Read today, the Key is best appreciated as design history and as a window into how Renaissance Europe imagined power — ceremonial, hierarchical, and deeply anxious about purity. For our readers, it is also useful context: the transatlantic folk traditions we cherish often borrowed these seals and bent them toward humbler, more human ends.",
      "Our verdict: a landmark, more admired than easily used. Come for the pentacles, stay for the glimpse of a mind trying to negotiate with the universe on paper.",
    ],
    downloads: [
      { label: "Mathers' translation (PDF)", url: "https://blog.philosophicalsociety.org/wp-content/uploads/2017/10/key-of-solomon-the-king.pdf" },
    ],
    sources: [
      { label: "Read the full text (Sacred Texts Archive)", url: "https://www.sacred-texts.com/grim/gkos/index.htm" },
      { label: "1889 first-edition scan (Archive.org / Wellcome)", url: "https://archive.org/details/b24884431" },
      { label: "Harvard Library — Digital Collections (search the occult holdings)", url: "https://library.harvard.edu/digital-collections" },
    ],
  },
  {
    id: "pow-wows-long-lost-friend",
    slug: "pow-wows-long-lost-friend",
    internal: true,
    category: "Sacred Scrolls · Review",
    title: "Pow-Wows; or, Long Lost Friend",
    excerpt:
      "Hohman's pocket-sized Pennsylvania Dutch grimoire of 1820 — charms for burns, bleeding, and bad luck. A review of American folk magic's most stubbornly useful little book.",
    author: "Veronica Bey",
    readTime: "6 min",
    image:
      "https://static.prod-images.emergentagent.com/jobs/6191983f-92f1-4de9-9adc-9e0de9357616/images/49fc312b0a6271133ac9001e5c2f1b38b8cfbfaa5f99ab1fc09d21cddea37edc.jpeg",
    imageCaption: "A household book — carried, consulted, and worn soft at the corners.",
    body: [
      "If the Key of Solomon is cathedral magic, Pow-Wows is kitchen magic. Compiled by Johann Georg Hohman and first printed in Pennsylvania in 1820, this slim volume of Braucherei — Pennsylvania Dutch folk healing — reads like a home-remedy book with the veil pulled slightly back: charms to stop bleeding, to cure a burn, to protect the house and the cattle, each braided with scripture and plain German piety.",
      "It is proudly practical. There is no grand cosmology here, only the conviction that a few right words, spoken with faith, can mend what needs mending. That intimacy is why the book survived: it was a working-class text, passed hand to hand, quoted from memory. It belongs to the same broad family as our own rootwork — traditions that trusted ordinary people, in ordinary kitchens, to tend the sacred themselves.",
      "The scholarship is delicious once you go looking: the tangled printing history, the German antecedents, the way American folklorists rediscovered it in the twentieth century and argued over what it meant. But the pleasure of the book is its voice — earnest, unbothered, sure.",
      "Our verdict: charming and genuinely important. Read it as social history and folk poetry, and keep it near the recipes where it has always belonged.",
    ],
    sources: [
      { label: "Read the full text (Sacred Texts Archive)", url: "https://sacred-texts.com/ame/pow/index.htm" },
      { label: "Full scan (Archive.org)", url: "https://archive.org/details/powwowsorlonglos0000john" },
      { label: "Stable text index (Online Books, U. Penn)", url: "https://onlinebooks.library.upenn.edu/webbin/book/lookupid?key=olbp19546" },
    ],
  },
  {
    id: "randolph-seership-magnetic-mirror",
    slug: "randolph-seership-magnetic-mirror",
    internal: true,
    category: "Sacred Scrolls · Review",
    title: "Seership! The Magnetic Mirror",
    excerpt:
      "Paschal Beverly Randolph — the free man of color who quietly founded American occultism — wrote a manual teaching anyone to see. A review of his mirror, his method, and the radical politics hidden inside a book about clairvoyance.",
    author: "Veronica Bey",
    readTime: "8 min",
    image:
      "https://static.prod-images.emergentagent.com/jobs/6191983f-92f1-4de9-9adc-9e0de9357616/images/c20d4c6a003db3bec02830596969ecc38654dec3e6ab436ae370ea227264759d.jpeg",
    imageCaption: "The magnetic mirror — Randolph's instrument for a sight he insisted belonged to everyone.",
    body: [
      "Before there was a Theosophical Society, before the Golden Dawn drew its first pentacle, there was Paschal Beverly Randolph — physician, abolitionist, medium, and the free-born son of a Black mother, moving through a nineteenth-century America that had no category for him. That he became one of the founders of Western occultism, and is still routinely written out of it, is the first thing any honest review of his work must say aloud.",
      "Seership! The Magnetic Mirror (Boston, 1870) is his most practical book: a guide to clairvoyance by means of the magic mirror — a dark, polished surface into which the trained eye learns to look and, Randolph promises, to truly see. He is precise about the instrument, the lighting, the patience required. Read alongside the grimoires of Europe, it is strikingly democratic. Where the Solomonic tradition guards its secrets behind Latin and hierarchy, Randolph writes for the ordinary reader and insists the gift of sight is native to the human soul.",
      "That insistence is politics disguised as method. A man who had been told his whole life what he could not be was writing a manual that said: you can perceive, you can know, you can author your own vision. For a Black esotericist in Reconstruction America, 'seership' was never only about crystal gazing. It was about the right to interior authority — to a self that could not be legislated.",
      "As a text, it is idiosyncratic and occasionally maddening; Randolph digresses, scolds his critics, and mingles trance instruction with wounded pride. But it rewards a patient, contextual reading. Take it with his biography and the recent scholarship, and the little book on mirrors becomes a window onto one of the most original minds this country ever failed to honor.",
      "Our verdict: indispensable, and overdue. Read it as instruction if you like, but read it above all as the testament of a Black visionary who taught a nation to look — and asked, quietly, to be seen in return.",
    ],
    downloads: [
      { label: "Full scan — Seership! (PDF, Archive.org)", url: "https://archive.org/download/seershipmagnetic00rand/seershipmagnetic00rand.pdf" },
    ],
    sources: [
      { label: "The item page & reader (Archive.org)", url: "https://archive.org/details/SeershipTheMagneticMirror" },
      { label: "Catalog record — public domain (Library of Congress)", url: "https://www.loc.gov/item/10033704/" },
      { label: "\u201cThe Emancipatory Visions of a Sex Magician\u201d (The Public Domain Review)", url: "https://publicdomainreview.org/essay/the-emancipatory-visions-of-a-sex-magician" },
      { label: "Life & legacy (Wikipedia)", url: "https://en.wikipedia.org/wiki/Paschal_Beverly_Randolph" },
    ],
  },
];

// CROSS-BRAND — Niiji Capital projects (niijicapital.com). Same founder: Veronica Bey.
export const NIIJI_ESSAYS = [
  {
    id: "ulyssia-sovereign-sea",
    slug: "ulyssia-sovereign-sea",
    internal: true,
    category: "Niiji Capital · Maritime",
    title: "The Sovereign Sea: Aboard Ulyssia",
    excerpt:
      "In partnership with Niiji Capital — the Ulyssia gigayacht, a 323-metre residential world at sea, and a new grammar of mobility as sovereignty.",
    author: "Veronica Bey",
    readTime: "7 min",
    image:
      "https://static.prod-images.emergentagent.com/jobs/b5e6bd01-eaf4-40ed-a42f-ee64e9f0206c/images/9f11c503ee2b7d0a1987efb419d78d5ae151fa875a93313e33413c52a4bdfb08.jpeg",
    imageCaption: "The Ulyssia gigayacht — advised by Niiji Consulting.",
    body: [
      "Niiji Capital's maritime advisory centers on Ulyssia — a 323-metre residential gigayacht envisioned by Frank Binder and designed by Espen Øino, comprising 122 private freehold residences and 22 guest suites that journey, in privacy, to every corner of the world.",
      "What draws us to it is not the spectacle but the thesis: that mobility, done with intention, becomes a form of sovereignty. To move freely — with a Chenot longevity clinic, polar expedition programmes, and submersibles aboard — is to author one's own geography. This is the through-line between Sybils & Society and Niiji Capital: heritage and return, engineered as one.",
    ],
    sources: [
      { label: "Niiji Capital — Ulyssia Advisory", url: "https://niijicapital.com/#expertise" },
    ],
  },
  {
    id: "xualla-conservation-reserve",
    slug: "xualla-conservation-reserve",
    internal: true,
    category: "Niiji Capital · Conservation",
    title: "Xualla: Luxury That Gives More Than It Takes",
    excerpt:
      "In partnership with Niiji Capital — a private conservation reserve where indigenous heritage, biodiversity, and renewable-powered design coexist as one.",
    author: "Veronica Bey",
    readTime: "6 min",
    image:
      "https://static.prod-images.emergentagent.com/jobs/b5e6bd01-eaf4-40ed-a42f-ee64e9f0206c/images/df119e0db30ac0b335e3de1cbe9147377a30b312be9a6a646cc39c7c0ec6b004.jpeg",
    imageCaption: "Xualla Resort — renewable dome architecture within a protected reserve.",
    body: [
      "Xualla is Niiji Capital's answer to a tired question. Set within a private reserve where more than seventy percent of the land is held in permanent conservation, it houses guests as stewards of a living ecosystem rather than visitors to it — organic, renewable-powered dome architecture with closed-loop water and a LEED Platinum pathway.",
      "Heritage here is the amenity: immersive cultural pavilions and wellness sanctuaries designed to honor indigenous knowledge and regional ecology. It is the same conviction this publication keeps returning to — that the deepest luxury is a legacy that outlasts us.",
    ],
    sources: [
      { label: "Niiji Capital — Xualla Reserve", url: "https://niijicapital.com/#expertise" },
    ],
  },
  {
    id: "beluga-bay-alaska",
    slug: "beluga-bay-alaska",
    internal: true,
    category: "Niiji Capital · Land & Legacy",
    title: "Beluga Bay: A Wilderness Held in Trust",
    excerpt:
      "In partnership with Niiji Capital — 144 acres on Alaska's Kenai Peninsula, overlooking Cook Inlet and the Chugach Mountains, framed as a legacy asset.",
    author: "Veronica Bey",
    readTime: "6 min",
    image:
      "https://static.prod-images.emergentagent.com/jobs/b5e6bd01-eaf4-40ed-a42f-ee64e9f0206c/images/c8bbc40e3a95f1399daa290500e097ed30ab78d0fbd91718f0dcbc2375da859e.jpeg",
    imageCaption: "Beluga Bay, Kenai Peninsula — a Niiji Capital land opportunity.",
    body: [
      "On the pristine shores of Beluga Bay — 144 acres of development land, 220 including the bluff — Niiji Capital stewards one of Alaska's rarest eco-luxury opportunities: commanding views over Cook Inlet and the Chugach Mountains, true wilderness fifteen minutes from an international airport.",
      "We write about it here because land, to us, is never only an asset. It is a trust — a relationship with place that either honors what came before or erases it. Niiji Capital's discipline is to make the honoring and the return the same act.",
    ],
    sources: [
      { label: "Niiji Capital — Beluga Bay", url: "https://niijicapital.com/#expertise" },
    ],
  },
];

// SYBILS SECRETS — Lifestyle & Astrology (on-site essays by Veronica Bey).
export const SECRETS_IMG = {
  hero: "https://images.unsplash.com/photo-1766879220930-5d4a4524d0e9?crop=entropy&cs=srgb&fm=jpg&w=1500&q=85&ixlib=rb-4.1.0",
  moon: "https://images.unsplash.com/photo-1655464205726-b337f82ec270?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85&ixlib=rb-4.1.0",
  portrait1: "https://images.unsplash.com/photo-1766879240364-d135fe50126e?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85&ixlib=rb-4.1.0",
  portrait2: "https://images.unsplash.com/photo-1766879240642-03c887de8e05?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85&ixlib=rb-4.1.0",
  eclipse: "https://images.unsplash.com/photo-1503416997304-7f8bf166c121?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85&ixlib=rb-4.1.0",
};

export const SECRETS_ESSAYS = [
  {
    id: "living-by-the-moon",
    slug: "living-by-the-moon",
    internal: true,
    category: "Lunar Living",
    title: "Living by the Moon",
    excerpt:
      "How to keep a matriarchal almanac — planting intentions at the new moon, releasing at the full, and letting the sky set the pace of a considered life.",
    author: "Veronica Bey",
    readTime: "6 min",
    image: SECRETS_IMG.moon,
    imageCaption: "The lunar cycle — the oldest calendar our grandmothers kept.",
    body: [
      "Long before there were planners and productivity apps, women kept time by the moon. The new moon was for beginnings — the quiet planting of an intention. The waxing weeks were for building. The full moon was for harvest and for release, and the dark of the moon was for rest, that most disobeyed of feminine disciplines.",
      "To live by the moon is not superstition; it is rhythm. It restores to a woman the sense that her energy is meant to rise and fall, not to run flat and relentless. The matriarch does not fight her own cycles — she governs by them.",
      "Begin simply. At each new moon, write one intention and speak it aloud. At each full moon, name one thing you are ready to set down. Keep the two lists side by side, and by year's end you will hold a private almanac of your own becoming.",
    ],
  },
  {
    id: "the-matriarchs-almanac",
    slug: "the-matriarchs-almanac",
    internal: true,
    category: "Astrology",
    title: "The Matriarch's Almanac: Reading Your Own Sky",
    excerpt:
      "An introduction to matriarchal astrology — the Sun you were given, the Moon you inherited, and the Rising you present to the world.",
    author: "Veronica Bey",
    readTime: "7 min",
    image: SECRETS_IMG.portrait1,
    imageCaption: "The chart as heirloom — a map of the sky at the hour you arrived.",
    body: [
      "Astrology, read well, is not a horoscope column. It is a map of the sky at the exact hour you arrived, and matriarchal traditions have always treated that map as an heirloom rather than a novelty.",
      "Three lights anchor the reading. The Sun is the self you were given — your essential fire. The Moon is the self you inherited — your inner tides, your ancestral memory, the mother-line coded into your feeling. The Rising is the self you present — the door through which the world first meets you.",
      "To read your own sky is to hold all three at once: what you burn with, what you carry, and how you cross the threshold into a room. The deeper reading — full transits, your daily fortune, the counsel of ancestral traditions — is kept within the Inner Sanctum of Sybils Society. Here, we simply teach you to look up.",
    ],
  },
  {
    id: "reading-the-sky",
    slug: "reading-the-sky",
    internal: true,
    category: "Ancestral Astronomy",
    title: "Reading the Sky as Our Grandmothers Did",
    excerpt:
      "Before almanacs were printed, they were remembered — the star knowledge of African and Indigenous women who timed planting, travel, and prayer by the heavens.",
    author: "Veronica Bey",
    readTime: "6 min",
    image: SECRETS_IMG.portrait2,
    imageCaption: "Star knowledge, kept in the body and passed hand to mouth.",
    body: [
      "Our grandmothers were astronomers, though few called them that. They read the sky for planting and for prayer, timed travel by the moon, and knew which stars meant the season had turned. The knowledge lived in the body and passed hand to mouth, uncredited by any academy.",
      "Across the Black and Indigenous Americas, celestial timekeeping was survival. Enslaved people watched the same heavens for the road north; Lowcountry elders read the tides against the moon. To look up was to orient oneself in a world that kept trying to disorient you.",
      "When we practice astrology now, we are not adopting something foreign. We are resuming an inheritance — taking back the right to read the sky and to let it counsel us, exactly as the women before us did.",
    ],
  },
  {
    id: "the-well-kept-self",
    slug: "the-well-kept-self",
    internal: true,
    category: "Lifestyle & Ritual",
    title: "The Well-Kept Self: A Lifestyle of Ritual",
    excerpt:
      "On unhurried living — the morning water, the tended altar, the evening reflection — and how small ceremonies become the architecture of a sovereign life.",
    author: "Veronica Bey",
    readTime: "5 min",
    image: SECRETS_IMG.eclipse,
    imageCaption: "The eclipse — a reminder that even the sky observes its seasons of rest.",
    body: [
      "A well-kept self is not a matter of luxury but of ritual. It is the glass of water taken slowly at dawn, the altar dusted and refreshed, the few minutes of quiet before the day is allowed to make its demands.",
      "Lifestyle, in the matriarchal register, is not consumption. It is ceremony — the small, repeated acts by which a woman tells herself that her days are worth ordering with intention. Beauty, rest, devotion, and attention are not indulgences to be earned; they are the architecture of a sovereign life.",
      "Draw your card, set your intention, and keep the ritual. The Oracle is only a mirror. What it reflects back is the self you have decided to keep well.",
    ],
  },
];

// THE REVIEW — high-fashion, matriarchal, spiritual editorial (on-site essays).
export const REVIEW_ESSAYS = [
  {
    id: "the-matriarch-wears-time",
    slug: "the-matriarch-wears-time",
    internal: true,
    category: "Matriarchy",
    title: "The Matriarch Wears Time",
    excerpt:
      "In our houses, style was never vanity — it was record-keeping. On adornment as lineage, and the woman who carries the ones before her into every room.",
    author: "Veronica Bey",
    readTime: "6 min",
    image: "https://static.prod-images.emergentagent.com/jobs/6191983f-92f1-4de9-9adc-9e0de9357616/images/a9dec110cdce1bd4151641c81890be149105151e10627f1923061fbbd51d2863.jpeg",
    body: [
      "In the houses that raised me, style was never vanity; it was record-keeping. A grandmother's brooch, a mother's headwrap, the particular way a collar was starched before church — each was a sentence in a language of belonging, spoken in cloth and gold.",
      "To dress as a matriarch is to wear time on the body. It is to carry the women who came before into every room you enter, and to leave, in the cut of a garment, quiet instructions for the girls who will follow. Elegance, in our tradition, is not display. It is continuity.",
      "This is the register The Oracle means to keep: fashion as inheritance, beauty as memory, and the sovereign woman as the living archive of her line.",
    ],
  },
  {
    id: "adornment-as-prayer",
    slug: "adornment-as-prayer",
    internal: true,
    category: "Spirit",
    title: "Adornment as Prayer",
    excerpt:
      "Gold at the throat, beads at the wrist, oil at the crown — a meditation on the sacred grammar of how we make ourselves ready to be seen.",
    author: "Veronica Bey",
    readTime: "5 min",
    image: IMG.portraitB,
    body: [
      "Before there were runways there were rituals: gold set at the throat, beads counted at the wrist, oil pressed into the crown. To adorn the body was to consecrate it — to declare that this vessel is worthy of attention, and that attention is a form of devotion.",
      "We inherited a spirituality of the surface, in the most exalted sense. What glitters on us is not distraction from the soul but a signal of it. The Oracle reads adornment as prayer, and the dressing table as a small, private altar.",
    ],
  },
  {
    id: "the-architecture-of-grace",
    slug: "the-architecture-of-grace",
    internal: true,
    category: "Fashion",
    title: "The Architecture of Grace",
    excerpt:
      "On tailoring as discipline — the sharp shoulder, the held line, and the quiet power of a woman who has decided exactly how she will occupy a room.",
    author: "Veronica Bey",
    readTime: "5 min",
    image: IMG.portraitA,
    body: [
      "There is an architecture to grace. It lives in the sharp shoulder of a well-cut jacket, in the held line of a hem, in the decision — made long before the door opens — of exactly how one intends to occupy a room.",
      "High fashion, at its finest, is not noise; it is engineering in service of presence. The Oracle honors the tailors, the seamstresses, and the sovereign women who understood that to structure a garment is to structure the way the world receives you.",
    ],
  },
  {
    id: "rituals-of-the-toilette",
    slug: "rituals-of-the-toilette",
    internal: true,
    category: "Ritual",
    title: "Rituals of the Toilette",
    excerpt:
      "The slow ceremony of getting ready — as heritage, as resistance, and as the most radical luxury of all: unhurried attention to oneself.",
    author: "Veronica Bey",
    readTime: "6 min",
    image: IMG.books,
    body: [
      "The toilette is a ceremony we have too often been taught to rush. Yet the slow work of readying oneself — the braiding, the anointing, the choosing — is heritage, and it is resistance. To take one's time is to insist that one's time is worth taking.",
      "The most radical luxury we can practice is unhurried attention to ourselves. The Oracle treats the ritual of getting ready as exactly that: a private liturgy, performed in the mirror, in a lineage of women who never once rushed their own becoming.",
    ],
  },
  {
    id: "the-inheritance-of-elegance",
    slug: "the-inheritance-of-elegance",
    internal: true,
    category: "Matriarchy",
    title: "The Inheritance of Elegance",
    excerpt:
      "How grace is passed down — not taught so much as witnessed — from the women who set the table, held the room, and never once explained themselves.",
    author: "Veronica Bey",
    readTime: "5 min",
    image: "/inheritance-champagne.jpg",
    imageCaption:
      "Contributor Veronica Bey at the Champagne Toast after-party for the executive team of LALOVAVI at the historic Cincinnati Music Hall.",
    body: [
      "Elegance is rarely taught. It is witnessed. We learned it from women who set the table with intention, who held a room without raising their voices, and who never once felt the need to explain themselves.",
      "That inheritance travels — across generations, and now across continents, into the salons of The Horizon Circle. The Oracle exists to name it, to honor it, and to make sure the next daughter recognizes it when she feels it arrive in her own hands.",
    ],
  },
];

export const LEAD = {
  ...REVIEW_ESSAYS[0],
  category: "The Essay",
  dek: REVIEW_ESSAYS[0].excerpt,
};

export const STORIES = [ESSAYS[0], ESSAYS.find((e) => e.id === "ai-behold-a-pale-horse"), ESSAYS[2], ARCHIVE_SPIRITUAL[0], ARCHIVE_FASHION[0]];

// Every internal essay, for the /essay/:slug reader.
export const ALL_ESSAYS = [
  ...REVIEW_ESSAYS,
  ...NIIJI_ESSAYS,
  ...SECRETS_ESSAYS,
  ...ARCHIVE_SPIRITUAL,
  ...ARCHIVE_FASHION,
  ...ARCHIVE_ART_CITIES,
  ...SACRED_SCROLLS,
  ...ESSAYS,
];

export const ARCHIVES = [
  {
    id: "arc-01",
    era: "MCMXII",
    title: "Letters from the Oracle Estate",
    excerpt:
      "Correspondence recovered from a private collection, annotated for the first time.",
    image: IMG.manuscript,
  },
  {
    id: "arc-02",
    era: "MCMXLVII",
    title: "The Salon Ledgers",
    excerpt:
      "Guest books and menus from a decade of clandestine literary gatherings.",
    image: IMG.books,
  },
  {
    id: "arc-03",
    era: "MCMLXX",
    title: "Portraits, Unsigned",
    excerpt:
      "An essay on anonymity, authorship, and the women left out of the frame.",
    image: IMG.portraitB,
  },
  {
    id: "arc-04",
    era: "MCMXCIV",
    title: "The Reading Rooms",
    excerpt:
      "A visual archive of the libraries that shaped a century of quiet influence.",
    image: IMG.library1,
  },
];

export const HORIZON_TIERS = [
  {
    id: "navigator",
    name: "The Navigator Circle",
    kicker: "Digital forums & virtual salons",
    price: "By application",
    body:
      "Entry into the conversation. Members-only digital forums, virtual salons, and the monthly dispatch on culture and global mobility.",
    perks: [
      "Private digital forum access",
      "Monthly virtual salons",
      "The Horizon dispatch",
    ],
    featured: false,
  },
  {
    id: "fellow",
    name: "The Horizon Fellow",
    kicker: "Boutique retreats & regional dinners",
    price: "By invitation",
    body:
      "For those who travel to belong. Boutique group retreats, regional culinary events, and priority standing for seasonal gatherings.",
    perks: [
      "Everything in Navigator",
      "Boutique group retreats",
      "Regional culinary events",
      "Priority event standing",
    ],
    featured: true,
  },
  {
    id: "apex",
    name: "The Apex Guild",
    kicker: "Concierge mobility & private pods",
    price: "Private",
    body:
      "The full concierge relationship. Bespoke global mobility, private institutional networking pods, and a dedicated curator.",
    perks: [
      "Everything in Fellow",
      "Concierge global mobility",
      "Private networking pods",
      "Dedicated curator",
    ],
    featured: false,
  },
];

export const EXPERIENCES = [
  {
    id: "exp-01",
    type: "Culinary Salon",
    title: "A Private Table in Oaxaca",
    location: "Oaxaca, Mexico",
    date: "Spring · By waitlist",
    tier: "The Horizon Fellow",
    image: IMG.culinary1,
    excerpt:
      "An intimate tasting with a heritage kitchen, hosted across two candlelit evenings.",
  },
  {
    id: "exp-02",
    type: "Heritage Retreat",
    title: "The Villa Sessions",
    location: "Amalfi Coast, Italy",
    date: "Early Summer · By waitlist",
    tier: "The Apex Guild",
    image: IMG.resort2,
    excerpt:
      "A boutique retreat on stewardship, legacy, and the art of slow travel.",
  },
  {
    id: "exp-03",
    type: "Virtual Salon",
    title: "Conversations on Global Mobility",
    location: "Online · The Circle",
    date: "Monthly · Open",
    tier: "The Navigator Circle",
    image: IMG.horizon2,
    excerpt:
      "A recurring salon with cultural leaders, curators, and the modern vanguard.",
  },
  {
    id: "exp-04",
    type: "International Gathering",
    title: "The Horizon Assembly",
    location: "Marrakech, Morocco",
    date: "Autumn · By invitation",
    tier: "The Apex Guild",
    image: IMG.resort1,
    excerpt:
      "The flagship gathering — a weekend of private dinners, salons, and networking pods.",
  },
];


// FROM THE INNER SANCTUM — gated gatherings of Sybils Society (the cosmic wing).
export const SANCTUM_EVENTS = [
  {
    id: "sanctum-mysteries",
    type: "The Matriarchal Mysteries",
    title: "The Bi-Annual Convening",
    cadence: "Twice yearly · location revealed 48 hours prior",
    access: "The Matriarch",
    image:
      "https://images.unsplash.com/photo-1536392706976-e486e2ba97af?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85&ixlib=rb-4.1.0",
    excerpt:
      "The Society's most sacred gathering — a candlelit convening of the circle whose place and hour are disclosed only to the initiated, two days before the doors open.",
  },
  {
    id: "sanctum-council",
    type: "The Sybils Council",
    title: "The Members' Mastermind",
    cadence: "Quarterly convenings · global circle",
    access: "The Adept & above",
    image:
      "https://images.unsplash.com/photo-1529516222410-a269d812f320?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85&ixlib=rb-4.1.0",
    excerpt:
      "A private circle of founders, executives, and matriarchs — peer advisory over networking, where capital is pooled and ancestral business is planned.",
  },
  {
    id: "sanctum-academies",
    type: "The Matriarchal Academies",
    title: "Immersive Retreats",
    cadence: "Seasonal · by tier",
    access: "The Adept & above",
    image:
      "https://images.unsplash.com/photo-1767050190883-29d644fa5b99?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85&ixlib=rb-4.1.0",
    excerpt:
      "Retreats in wealth stewardship, intergenerational asset transfer, somatic leadership, and energetic sovereignty — the curriculum of a lasting matriarchy.",
  },
  {
    id: "sanctum-oracle",
    type: "Private Audience with the Oracle",
    title: "The Strategic Council",
    cadence: "By appointment",
    access: "All initiates",
    image:
      "https://images.unsplash.com/photo-1606659894125-40824878b6ce?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85&ixlib=rb-4.1.0",
    excerpt:
      "A one-to-one audience where lineage is read and blocked power unlocked — strategic counsel drawn from the archetypes of the matriarchs.",
  },
];
